import { getCached, list } from "common/api/activatedPriceList.service";
import { ThunkResult } from "common/reducers";
import { handleErrors } from "common/reducers/Utils";
import { ActivatedPriceListActions } from "common/reducers/activatedPriceList.actions";
import { RootDispatchType, RootGetState } from "common/store";
import { currentOrgErrors, currentOrgId } from "common/selectors/org.selectors";
import { addFlashAlert } from "common/components/FlashAlert";
import { IdsManagementActions } from "common/reducers/idsManagement.actions";
import { activatedPriceListParamsFromJSON } from "common/records/index/ActivatedPriceListParams";
import { watchRedux } from "src/router/loaders/helper";
import {
  activatedPriceListById,
  currentOrgActivatedPriceListId,
  fannedActivatedPriceListId,
} from "common/selectors/activatedPriceList.selectors";
import { List } from "immutable";

export const ActivatedPriceListAsyncActions = {
  listActivatedPriceLists: (priceListId: number, page = 1, perPage = 10): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      const orgId = currentOrgId(getState());
      if (!orgId) {
        addFlashAlert("danger", "Please select an org before continuing");
        return;
      }
      const modelName = `activatedPriceLists.${priceListId}`;
      const params = activatedPriceListParamsFromJSON({ orgId, price_list_id: priceListId, page, per_page: perPage });

      dispatch(IdsManagementActions.fetchModel(modelName, params));
      try {
        const response = await list(params);
        dispatch(ActivatedPriceListActions.receiveActivatedPriceLists(response.activated_price_lists));
        dispatch(
          IdsManagementActions.receiveModel(
            modelName,
            List(response.activated_price_lists.map((apl) => apl.id)),
            response.meta?.pagination,
          ),
        );
      } catch (response) {
        handleErrors(response, true);
      }
    };
  },
  /**
   * Loads the cached snapshot for an activated_price_list_id. Two calls: GET .../cached for the
   * signed file URL, then a bare `fetch(file.url)` (no Authorization — the bearer token would fail
   * the signed-URL signature check). Short-circuits only when this APL is already cached AND its
   * snapshot is the one currently fanned into the shared global stores; otherwise re-fans. Returns
   * true if loaded or already present, false on any failure (missing org/id/URL, non-OK fetch,
   * thrown error).
   */
  getActivatedPriceList: (activatedPriceListId: number): ThunkResult<Promise<boolean>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      const orgId = currentOrgId(getState());
      if (!orgId) {
        return false;
      }
      if (!activatedPriceListId) {
        return false;
      }
      // Short-circuit only when this APL is cached AND its snapshot is the one currently fanned into
      // the shared globals (see fannedActivatedPriceListId); otherwise fall through and re-fan.
      const existing = activatedPriceListById(getState(), activatedPriceListId);
      const fannedId = fannedActivatedPriceListId(getState());
      if (
        existing &&
        existing.id === activatedPriceListId &&
        existing.cached === true &&
        fannedId === activatedPriceListId
      ) {
        return true;
      }
      try {
        const { activated_price_list } = await getCached(orgId, activatedPriceListId);
        const url = activated_price_list.file?.file?.url;
        if (!url) {
          return false;
        }
        // Bare `fetch` sends no Authorization header (unlike `fetcher`), as the signed URL requires.
        const response = await fetch(url);
        if (!response.ok) {
          return false;
        }
        const { price_list } = await response.json();
        // A malformed file (no `price_list`/`products` array) would never flip `cached`, sticking
        // the dropdown on "Loading products…"; treat it as a failure. Empty `products: []` is valid.
        if (!price_list || !Array.isArray(price_list.products)) {
          return false;
        }
        // The cached file is a serialized PRICE LIST: its `id` is the price_list id, not the
        // activated_price_list id every caller keys by. Re-key it under the requested id (else the
        // snapshot lands under the wrong key and the dropdown stays "Loading products…"), and carry
        // the price_list id through to `price_list_id`.
        dispatch(
          ActivatedPriceListActions.receiveActivatedPriceList({
            ...price_list,
            id: activatedPriceListId,
            price_list_id: activated_price_list.price_list_id ?? price_list.id,
          }),
        );
        return true;
      } catch (e) {
        handleErrors(e);
        return false;
      }
    };
  },

  /**
   * Wraps getActivatedPriceList for the Org Products tab loader (current org's APL). Flashes a
   * danger alert when no price list is configured or the snapshot can't be fetched.
   */
  getOrgPriceList: (): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      const errorMessage = "No price list found for this org";
      const activatedPriceListId = await watchRedux(currentOrgActivatedPriceListId, () => List([errorMessage]));
      const orgId = currentOrgId(getState());
      if (!orgId) {
        addFlashAlert("danger", "Please select an org before continuing");
        return;
      }
      if (!activatedPriceListId) {
        addFlashAlert("danger", errorMessage);
        return;
      }
      const loaded = await dispatch(
        ActivatedPriceListAsyncActions.getActivatedPriceList(activatedPriceListId as number),
      );
      if (!loaded) {
        addFlashAlert("danger", errorMessage);
      }
    };
  },
};
